import { useCallback, useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { ReviewAnswerCard } from "@/components/portal/lms/ReviewAnswerCard";
import { needsScore, resultNotifyState } from "@/components/portal/lms/reviewRules";
import { useReviewDirectory } from "@/components/portal/lms/useReviewDirectory";
import {
  formatUtcNaive,
  lmsReviewApi,
  unscoredQuestionIds,
  type ReviewAnswerPatch,
  type ReviewAttempt,
} from "@/services/api/lms";
import { PortalApiError } from "@/lib/portalApi";

export const Route = createFileRoute("/portal/learning/review/$attemptId")({
  component: ReviewAttemptPage,
});

const STATE_LABEL: Record<string, string> = {
  auto_graded: "Awaiting review",
  under_review: "In review",
  final: "Final",
};

function percent(value: string | null): string {
  return value === null ? "—" : `${Number(value)}%`;
}

function pendingText(unscored: number): string {
  if (unscored === 0) return "Ready to finalise";
  const noun = unscored === 1 ? "answer" : "answers";
  return `${unscored} free-text ${noun} to score`;
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

/**
 * The review screen for one attempt. Everything shown comes from dp lms's
 * reviewer DTO — any miss (another team, the caller's own attempt, unknown id)
 * is a 404 from the API, rendered here as "not found" with no further detail.
 *
 * Editable only while `under_review`. A save returns the whole attempt, which
 * replaces local state, so what the reviewer sees is always the server's copy.
 * Finalise is terminal; its 409 detail is shown verbatim, and when it names
 * unscored questions those cards are flagged.
 *
 * On a `final` attempt the outcome card reports the result email from dp's
 * `result_notified_at_utc` marker (sent / sending / not sent, see
 * `resultNotifyState`), and "Resend result" — shown whenever the marker is null
 * — calls finalise again: dp re-fires the follow-up on a final attempt and
 * awaits an in-flight send rather than duplicating it. A failed finalise
 * re-reads the attempt (a 503 "Result saved; follow-up could not be queued"
 * leaves it `final`) and keeps dp's detail on the page, so the retry survives a
 * reload: the attempt URL is the handle.
 */
function ReviewAttemptPage() {
  const { attemptId } = Route.useParams();
  const { token } = useAuth();
  const directory = useReviewDirectory();

  const [attempt, setAttempt] = useState<ReviewAttempt | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingSaves, setPendingSaves] = useState(0);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [flagged, setFlagged] = useState<Set<string>>(new Set());
  const [resetToken, setResetToken] = useState(0);
  const [finaliseError, setFinaliseErrorState] = useState<string | null>(() =>
    readUnqueued(attemptId),
  );
  const [now, setNow] = useState(() => Date.now());
  const [sentAt, setSentAt] = useState<number | null>(null);

  // A finalise 503 means dp saved the result but queued no email. Nothing on
  // the attempt says so, so the detail is kept per attempt for this tab and
  // survives a reload — otherwise the page would read "Sending result…".
  const setFinaliseError = useCallback(
    (message: string | null) => {
      setFinaliseErrorState(message);
      writeUnqueued(attemptId, message);
    },
    [attemptId],
  );

  const load = useCallback(async () => {
    if (!token) return;
    try {
      setAttempt(await lmsReviewApi.getReview(token, attemptId));
      setLoadError(null);
    } catch (err) {
      const notFound = err instanceof PortalApiError && err.status === 404;
      setLoadError(notFound ? "Attempt not found." : errorMessage(err, "Failed to load"));
    }
  }, [token, attemptId]);

  useEffect(() => {
    load();
  }, [load]);

  // The in-flight poll keeps the last good attempt on a failed re-read: a
  // network blip must not replace the result screen with "not found".
  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      setAttempt(await lmsReviewApi.getReview(token, attemptId));
    } catch {
      // keep what is on screen; the next tick retries
    }
  }, [token, attemptId]);

  // The marker can land by another route (another tab's resend, an ops
  // re-finalise, a gateway 5xx on a finalise that succeeded): once dp says the
  // result was emailed, the stored "finalise again to retry" no longer holds.
  const notifiedAt = attempt?.result_notified_at_utc ?? null;
  useEffect(() => {
    if (notifiedAt) setFinaliseError(null);
  }, [notifiedAt, setFinaliseError]);

  const save = useCallback(
    async (questionId: string, patch: ReviewAnswerPatch) => {
      if (!token) return;
      setPendingSaves((n) => n + 1);
      try {
        const updated = await lmsReviewApi.saveReviewAnswer(
          token,
          attemptId,
          questionId,
          patch,
        );
        setAttempt(updated);
        setFlagged((prev) => {
          if (!prev.has(questionId)) return prev;
          const next = new Set(prev);
          next.delete(questionId);
          return next;
        });
      } catch (err) {
        toast.error(errorMessage(err, "Could not save the mark"));
        // The server copy is the truth — re-read it, and bump resetToken so the
        // card reverts even when that copy is unchanged by the refused edit.
        await load();
        setResetToken((n) => n + 1);
      } finally {
        setPendingSaves((n) => n - 1);
      }
    },
    [token, attemptId, load],
  );

  const start = async () => {
    if (!token) return;
    setBusy(true);
    try {
      setAttempt(await lmsReviewApi.startReview(token, attemptId));
    } catch (err) {
      toast.error(errorMessage(err, "Could not start the review"));
    } finally {
      setBusy(false);
    }
  };

  const finalise = async (resend = false) => {
    if (!token) return;
    setConfirmOpen(false);
    setBusy(true);
    try {
      setAttempt(await lmsReviewApi.finaliseReview(token, attemptId));
      setFlagged(new Set());
      setFinaliseError(null);
      // Anchor the grace window on this browser's clock for every finalise
      // made here, so clock skew against dp's reviewed_at cannot misread it.
      const at = Date.now();
      setNow(at);
      setSentAt(at);
      toast.success(
        resend
          ? "Sending the result to the learner again."
          : "Review finalised — the result is released to the learner.",
      );
    } catch (err) {
      const message = errorMessage(err, "Could not finalise");
      toast.error(message);
      setFlagged(new Set(unscoredQuestionIds(err)));
      // The server's state is the truth: a 503 here has already made the
      // attempt final. Re-read it so the page shows Resend, and keep the detail
      // on screen (a toast is gone before anyone acts on it).
      if (err instanceof PortalApiError && err.status >= 500) setFinaliseError(message);
      // refresh, not load: a failed re-read keeps the screen rather than
      // replacing it with "Failed to load".
      await refresh();
      setNow(Date.now());
    } finally {
      setBusy(false);
    }
  };

  // While the result email is in flight (after the review, or after a resend —
  // finalise answers before the worker sends), re-read the attempt every 20 s so
  // the marker shows up when it lands, and so "Sending…" turns into "not sent"
  // at the grace boundary.
  const notify =
    attempt?.state === "final"
      ? finaliseError && !attempt.result_notified_at_utc
        ? "not_sent"
        : resultNotifyState(attempt.reviewed_at_utc, attempt.result_notified_at_utc, now, sentAt)
      : null;
  useEffect(() => {
    if (notify !== "sending") return;
    const timer = setInterval(() => {
      setNow(Date.now());
      void refresh();
    }, 20_000);
    return () => clearInterval(timer);
  }, [notify, refresh]);

  if (loadError) {
    return (
      <Card>
        <CardContent className="py-12 text-center" data-testid="lms-review-not-found">
          <p className="text-destructive">{loadError}</p>
          <Button asChild variant="outline" className="mt-4">
            <Link to="/portal/learning/review">Back to the queue</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!attempt) {
    return (
      <div className="flex justify-center py-12 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const isFinal = attempt.state === "final";
  const editable = attempt.state === "under_review";
  const unscored = attempt.questions.filter(needsScore).length;
  const canFinalise = editable && unscored === 0 && pendingSaves === 0 && !busy;
  const questions = [...attempt.questions].sort(
    (a, b) => a.display_order - b.display_order,
  );

  return (
    <>
      <Helmet>
        <title>{`Review: ${attempt.test_title} | Portal`}</title>
      </Helmet>

      <div className="space-y-6" data-testid="lms-review-screen" data-state={attempt.state}>
        <Link
          to="/portal/learning/review"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to the queue
        </Link>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold">{attempt.test_title}</h1>
            <p className="text-sm text-muted-foreground">
              {directory.learnerName(attempt.learner_user_id)} ·{" "}
              {directory.teamName(attempt.team_id)} · attempt {attempt.attempt_number} ·
              submitted {formatUtcNaive(attempt.submitted_at_utc)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={isFinal ? "default" : "secondary"} data-testid="lms-review-state">
              {STATE_LABEL[attempt.state] ?? attempt.state}
            </Badge>
            {attempt.state === "auto_graded" && (
              <Button onClick={start} disabled={busy} data-testid="lms-review-start">
                Start review
              </Button>
            )}
            {editable && (
              <Button
                onClick={() => setConfirmOpen(true)}
                disabled={!canFinalise}
                aria-describedby="lms-review-pending"
                data-testid="lms-review-finalise"
              >
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Finalise
              </Button>
            )}
          </div>
        </div>

        {finaliseError && (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
            data-testid="lms-review-finalise-error"
          >
            {finaliseError}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          <Card>
            <CardContent className="pt-6">
              <p className="text-xs text-muted-foreground">Auto score (incl. proposed)</p>
              <p className="text-xl font-semibold">{percent(attempt.auto_score_percent)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-xs text-muted-foreground">Pass mark</p>
              <p className="text-xl font-semibold">{percent(attempt.pass_mark_percent)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6" data-testid="lms-review-outcome">
              <p className="text-xs text-muted-foreground">Result</p>
              {isFinal ? (
                <p className="flex items-center gap-2 text-xl font-semibold">
                  {percent(attempt.final_score_percent)}
                  <Badge variant={attempt.is_pass ? "success" : "destructive"}>
                    {attempt.is_pass ? "Pass" : "Fail"}
                  </Badge>
                </p>
              ) : (
                <p className="text-sm text-muted-foreground" id="lms-review-pending">
                  {pendingText(unscored)}
                </p>
              )}
              {isFinal && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Reviewed {formatUtcNaive(attempt.reviewed_at_utc)}
                  {attempt.period_label ? ` · period ${attempt.period_label}` : ""}
                </p>
              )}
              {isFinal && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="text-xs" data-testid="lms-review-notify" data-notify={notify ?? undefined}>
                    {notify === "sent"
                      ? `Result emailed ${formatUtcNaive(attempt.result_notified_at_utc ?? null)}`
                      : notify === "sending"
                        ? "Sending result…"
                        : "Result not sent"}
                  </span>
                  {!attempt.result_notified_at_utc && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => finalise(true)}
                      disabled={busy}
                      data-testid="lms-review-resend"
                    >
                      {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Resend result
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          {questions.map((question, index) => (
            <ReviewAnswerCard
              key={question.id}
              question={question}
              position={index + 1}
              editable={editable}
              flagged={flagged.has(question.id)}
              resetToken={resetToken}
              onSave={save}
            />
          ))}
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Finalise this review?</AlertDialogTitle>
            <AlertDialogDescription>
              The score, pass/fail and your comments are released to the learner.
              A finalised review cannot be changed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => finalise()} data-testid="lms-review-finalise-confirm">
              Finalise
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

const UNQUEUED_KEY = "lms-review-unqueued:";

function readUnqueued(attemptId: string): string | null {
  try {
    return window.sessionStorage.getItem(UNQUEUED_KEY + attemptId);
  } catch {
    return null;
  }
}

function writeUnqueued(attemptId: string, message: string | null): void {
  try {
    if (message) window.sessionStorage.setItem(UNQUEUED_KEY + attemptId, message);
    else window.sessionStorage.removeItem(UNQUEUED_KEY + attemptId);
  } catch {
    // storage unavailable: the alert lasts until reload
  }
}
